import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  message,
  Row,
  Col,
  Radio,
  Spin,
  Divider,
} from "antd";
import axios from "axios";
import {baseURL} from "./api"

const { Option } = Select;

const CustomerPricing = () => {
  const [vendors, setVendors] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [menuItemsByRestaurant, setMenuItemsByRestaurant] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [customerProductPricing, setCustomerProductPricing] = useState([]);
  const [role, setRole] = useState(null);
  const [userType, setUserType] = useState("existing");
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/getVendor`
        );
        setVendors(response.data);
        const vendorId = localStorage.getItem("vendorId");
        const roleFromStorage = localStorage.getItem("role");
        setRole(roleFromStorage);
        if (vendorId) setSelectedVendor(vendorId);
      } catch (error) {
        message.error("Error fetching vendors.");
      }
    };
    fetchVendors();
  }, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!selectedVendor) return;
      setLoadingCustomers(true);
      try {
        const response = await axios.get(
          `${baseURL}/getCustomerPrices/${selectedVendor}/null`
        );
        setExistingCustomers(response.data);
      } catch (error) {
        message.error("Error fetching customers.");
      } finally {
        setLoadingCustomers(false);
      }
    };

    if (userType === "existing" && selectedVendor) {
      fetchCustomers();
    }

    setSelectedCustomer(null);
    setRestaurants([]);
    setMenuItemsByRestaurant({});
    setCustomerProductPricing([]);
  }, [userType, selectedVendor]);

  useEffect(() => {
    const fetchRestaurantsAndMenu = async () => {
      if (!selectedVendor || (userType === "existing" && !selectedCustomer))
        return;

      try {
        const restResponse = await axios.get(
          `${baseURL}/getRestaurant`
        );
        const allRestaurants = restResponse.data;

        const filteredRestaurants = allRestaurants.filter((r) =>
          role === "admin"
            ? r.default === true
            : r.vendorId === selectedVendor || r.default === true
        );
        setRestaurants(filteredRestaurants);

        const menuItemsMap = {};

        for (const rest of filteredRestaurants) {
          const menuResponse = await axios.get(
            `${baseURL}/getMenuItems`,
            {
              params: {
                vendorId: selectedVendor,
                restaurantId: rest._id,
              },
            }
          );
          menuItemsMap[rest._id] = menuResponse.data;
        }

        setMenuItemsByRestaurant(menuItemsMap);
      } catch (error) {
        message.error("Error fetching restaurants or menu items.");
      }
    };

    fetchRestaurantsAndMenu();
  }, [selectedVendor, selectedCustomer, userType, role]);

  const handleVendorChange = (value) => {
    setSelectedVendor(value);
    setSelectedCustomer(null);
    setCustomerProductPricing([]);
    setMenuItemsByRestaurant({});
    setExistingCustomers([]);
  };

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomer(customerId);
    setCustomerProductPricing([]);
  };

  const handlePriceChange = (restaurantId, menuItemId, value) => {
    setCustomerProductPricing((prev) => {
      const key = `${restaurantId}-${menuItemId}`;
      const existing = prev.find((item) => item.key === key);
      if (existing) {
        return prev.map((item) =>
          item.key === key ? { ...item, salePrice: value } : item
        );
      } else {
        return [
          ...prev,
          {
            key,
            restaurantId,
            menuItemId,
            salePrice: value,
          },
        ];
      }
    });
  };

  const onFinish = async (values) => {
    if (!selectedVendor) {
      message.error("Please select vendor.");
      return;
    }

    if (customerProductPricing.length === 0) {
      message.error("Please enter pricing for the products.");
      return;
    }

    const isNewCustomer = userType === "new";

    const customerName = isNewCustomer
      ? values.name
      : existingCustomers.find((c) => c._id === selectedCustomer)?.customerName;

    const customerContact = isNewCustomer
      ? values.contactNumber
      : existingCustomers.find((c) => c._id === selectedCustomer)?.customerContact;

    const groupedByRestaurant = {};

    customerProductPricing.forEach((item) => {
      if (!groupedByRestaurant[item.restaurantId]) {
        groupedByRestaurant[item.restaurantId] = [];
      }

      const menuItem = menuItemsByRestaurant[item.restaurantId]?.find(
        (m) => m._id === item.menuItemId
      );

      groupedByRestaurant[item.restaurantId].push({
        itemId: item.menuItemId,
        itemName: menuItem?.name || "",
        salePrice: item.salePrice,
      });
    });

    const pricingData = Object.keys(groupedByRestaurant).map((restId) => ({
      restaurantId: restId,
      items: groupedByRestaurant[restId],
    }));

    const payload = {
      vendorId: selectedVendor,
      customerName,
      customerContact,
      pricingData,
    };

    const url = isNewCustomer
      ? `${baseURL}/addCustomerProductPricing`
      : `${baseURL}/updateCustomerProductPricing/${selectedCustomer}`;

    const method = isNewCustomer ? "post" : "put";

    try {
      const response = await axios[method](url, payload);

      if (response.status === 200 || response.status === 201) {
        message.success(
          `Customer-specific pricing ${isNewCustomer ? "saved" : "updated"} successfully!`
        );
        form.resetFields();
        setCustomerProductPricing([]);
        setSelectedCustomer(null);
        setMenuItemsByRestaurant({});
      }
    } catch (error) {
      message.error("Error saving customer-specific pricing.");
    }
  };

  return (
    <div>
      <Radio.Group
        onChange={(e) => {
          setUserType(e.target.value);
          setCustomerProductPricing([]);
          form.resetFields();
        }}
        value={userType}
        style={{ marginBottom: 20 }}
      >
        <Radio value="existing">Existing Customer</Radio>
        <Radio value="new">New Customer</Radio>
      </Radio.Group>

      <Form form={form} onFinish={onFinish} layout="vertical">
        {userType === "new" && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Customer Name"
                name="name"
                rules={[{ required: true, message: "Please enter customer name" }]}
              >
                <Input placeholder="Enter Customer Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Contact Number"
                name="contactNumber"
                rules={[
                  { required: true, message: "Please enter contact number" },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Must be 10 digits",
                  },
                ]}
              >
                <Input placeholder="Enter Contact Number" maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
        )}

        {role !== "vendor" && (
          <Form.Item label="Select Vendor" required>
            <Select
              placeholder="Select Vendor"
              onChange={handleVendorChange}
              value={selectedVendor}
            >
              {vendors.map((vendor) => (
                <Option key={vendor._id} value={vendor._id}>
                  {vendor.vendorName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {userType === "existing" && selectedVendor && (
          <Form.Item label="Select Customer" required>
            {loadingCustomers ? (
              <Spin />
            ) : existingCustomers.length > 0 ? (
              <Select
                placeholder="Select existing customer"
                onChange={handleCustomerSelect}
                value={selectedCustomer}
              >
                {existingCustomers.map((cust) => (
                  <Option key={cust._id} value={cust._id}>
                    {cust.customerName}
                  </Option>
                ))}
              </Select>
            ) : (
              <span>No customers available</span>
            )}
          </Form.Item>
        )}

        {Object.keys(menuItemsByRestaurant).length > 0 && (
          <>
            <h3>Enter Customer-Specific Sale Price</h3>
            {Object.entries(menuItemsByRestaurant).map(([restaurantId, items]) => {
              const rest = restaurants.find((r) => r._id === restaurantId);
              return (
                <div key={restaurantId}>
                  <Divider orientation="left">{rest?.name || "Unnamed Brand"}</Divider>
                  {items.map((food) => (
                    <Row key={food._id} gutter={16} style={{ marginBottom: 8 }}>
                      <Col span={12}>
                        <Input disabled value={food.name} />
                      </Col>
                      <Col span={12}>
                        <Input
                          placeholder="Sale Price"
                          value={
                            customerProductPricing.find(
                              (item) =>
                                item.restaurantId === restaurantId &&
                                item.menuItemId === food._id
                            )?.salePrice || ""
                          }
                          onChange={(e) =>
                            handlePriceChange(restaurantId, food._id, e.target.value)
                          }
                          onKeyPress={(e) => {
                            if (!/[0-9.]/.test(e.key)) e.preventDefault();
                          }}
                        />
                      </Col>
                    </Row>
                  ))}
                </div>
              );
            })}
          </>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Save Customer Pricing
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CustomerPricing;
